import { notification } from 'antd'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import { NamePath } from 'antd/es/form/interface'
import { FC, useCallback, useContext, useRef } from 'react'
import Machine from '../../../../../../components/form-items/machine/machine'
import { EditableCellCtx } from '../../../../../../components/editable-cell/editable-cell'
import { hasDmsInFinishing } from '../../../../../../helpers/is-dms-machine'
import { useGetMachinesQuery } from '../../../../../../store/api/machines-api'
import CalculationPosition from '../../../../../../types/api/calculation-position'
import { CalculationPageCtx } from '../../../../calculations-page'

interface AdditionalMachinesWrapperProps {
    record?: CalculationPosition
}

const finishingMachinesFilter = { machine_type: { name: { $contains: 'finishing' } } }

const AdditionalMachinesWrapper: FC<AdditionalMachinesWrapperProps> = ({ record }) => {
    const ctx = useContext(EditableCellCtx)
    const form = useFormInstance()
    const { onUpdateCalculationPositionProperty } = useContext(CalculationPageCtx)
    const initialMachinesRef = useRef(record?.additional_machines)

    const { data } = useGetMachinesQuery({
        filters: finishingMachinesFilter,
        sort: ['name:asc'],
    })

    const handleBlur = useCallback(() => {
        const newMachineIds = (form.getFieldValue(['additional_machines', 'set']) || []) as string[]
        const previousMachines = initialMachinesRef.current || []
        const oldHadDms = hasDmsInFinishing(previousMachines)

        const keptMachines = previousMachines.filter(m => newMachineIds.includes(m.documentId))
        const addedIds = newMachineIds.filter(
            id => !previousMachines.some(m => m.documentId === id)
        )
        const addedMachines = addedIds
            .map(id => data?.data?.find(m => m.documentId === id))
            .filter((m): m is NonNullable<typeof m> => Boolean(m))
        const newHasDms = hasDmsInFinishing([...keptMachines, ...addedMachines])

        const resetLacquersDms = Boolean(record?.lacquersDms)
        const resetDmsReserve = record?.dmsReserve != null

        if (oldHadDms && !newHasDms && record && (resetLacquersDms || resetDmsReserve)) {
            const list: [NamePath, unknown][] = [[['additional_machines', 'set'], newMachineIds]]
            if (resetLacquersDms) {
                notification.warning({
                    message: 'Лак DMS сброшен',
                    description: 'Изменена отделка — поле доступно только при станке DMS.',
                })
                list.push(['lacquersDms', null])
            }
            if (resetDmsReserve) {
                notification.warning({
                    message: 'Запас DMS сброшен',
                    description: 'Изменена отделка — поле доступно только при станке DMS.',
                })
                list.push(['dmsReserve', null])
            }

            void onUpdateCalculationPositionProperty(record, { list })
            ctx.onCancel()
            return
        }

        ctx.onBlur()
    }, [ctx, data?.data, form, onUpdateCalculationPositionProperty, record])

    return (
        <Machine
            name={['additional_machines', 'set']}
            filters={finishingMachinesFilter}
            unique
            noStyle
            autoFocus
            mode={'multiple'}
            onBlur={handleBlur}
        />
    )
}

export default AdditionalMachinesWrapper
